import {
  DatagridConfigurable,
  TextField,
  FunctionField,
  useListContext
} from 'react-admin';
import { Tooltip } from '@mui/material';
import { TotalRows } from '../../../helperComponents/TotalRows';
import { EmptyBulkActionButtons } from '../../../helperComponents/EmptyBulkActionButtons';
import { DateInOneRow } from '../../../helperComponents/DateInOneRow';
import { useRowStyle } from '../../../../hooks/useRowStyle';
import { useBrands } from '../../../../hooks/useBrands';
import { MetaApiData } from '../../../../helpers/constants';
import { findDataSourceName } from '../../../../utils/apiUtils';
import { formatJson } from '../../../../utils/jsonUtils';
import { getColumnLabel, getSettingsInfo } from '../../../../utils/meta/metaColumnsUtils';
import { metaApiDataColumns } from '../../../../config/constants/meta/metaApiDataTableConstants';
import { renderMultipleBrandsWithNames, renderBrandWithName } from '../../../../utils/brandUtils';

export const MetaApiDataGrid = ({ isAggregated = false, rowClick }) => {
  const rowClassName = useRowStyle();
  const { total } = useListContext();
  const { brands } = useBrands();

  return (
    <>
      <TotalRows total={total} />
      <DatagridConfigurable
        bulkActionButtons={<EmptyBulkActionButtons />}
        preferenceKey="meta_api_data.datagrid"
        omit={MetaApiData.defaultOffColumns}
        className={`${rowClassName} datagrid-content`}
        rowClick={rowClick}
      >
        {isAggregated ? (
          <TextField source="count" label="Count" />
        ) : (
          <TextField source={metaApiDataColumns.id.source} label={getColumnLabel(metaApiDataColumns.id)} />
        )}
        <FunctionField
          label={getColumnLabel(metaApiDataColumns.data_source)}
          source={isAggregated ? metaApiDataColumns.settings.source : metaApiDataColumns.data_source.source}
          render={(record) =>
            isAggregated
              ? getSettingsInfo(record.settings)
              : findDataSourceName(record[metaApiDataColumns.data_source.source])
          }
        />
        <TextField source={metaApiDataColumns.settings_id.source} label={getColumnLabel(metaApiDataColumns.settings_id)} />
        <TextField source={metaApiDataColumns.pixel_id.source} label={getColumnLabel(metaApiDataColumns.pixel_id)} />
        <FunctionField
          label={getColumnLabel(metaApiDataColumns.brand_ids)}
          source={isAggregated ? metaApiDataColumns.brand_ids.source : metaApiDataColumns.brand_id.source}
          render={(record) =>
            isAggregated
              ? renderMultipleBrandsWithNames(record, brands)
              : renderBrandWithName(record, brands)
          }
        />
        <TextField source={metaApiDataColumns.event_source_url.source} label={getColumnLabel(metaApiDataColumns.event_source_url)} />
        <TextField source={metaApiDataColumns.domain_path.source} label={getColumnLabel(metaApiDataColumns.domain_path)} />
        <TextField source={metaApiDataColumns.order_id.source} label={getColumnLabel(metaApiDataColumns.order_id)} />
        <TextField source={metaApiDataColumns.platform_event_id.source} label={getColumnLabel(metaApiDataColumns.platform_event_id)} />
        <TextField source={metaApiDataColumns.internal_account_id.source} label={getColumnLabel(metaApiDataColumns.internal_account_id)} />
        <TextField source={metaApiDataColumns.account_id.source} label={getColumnLabel(metaApiDataColumns.account_id)} />
        <TextField source={metaApiDataColumns.account_name.source} label={getColumnLabel(metaApiDataColumns.account_name)} />
        <TextField source={metaApiDataColumns.campaign_id.source} label={getColumnLabel(metaApiDataColumns.campaign_id)} />
        <TextField source={metaApiDataColumns.transaction_id.source} label={getColumnLabel(metaApiDataColumns.transaction_id)} />
        <TextField source={metaApiDataColumns.event_name.source} label={getColumnLabel(metaApiDataColumns.event_name)} />
        <TextField source={metaApiDataColumns.value.source} label={getColumnLabel(metaApiDataColumns.value)} />
        <TextField source={metaApiDataColumns.payout_percentage.source} label={getColumnLabel(metaApiDataColumns.payout_percentage)} />
        <DateInOneRow
          source={metaApiDataColumns.event_time.source}
          label={getColumnLabel(metaApiDataColumns.event_time)}
          showTime={true}
        />
        <DateInOneRow
          source={metaApiDataColumns.event_time_converted.source}
          label={getColumnLabel(metaApiDataColumns.event_time_converted)}
          showTime={true}
        />
        {isAggregated && 
          <DateInOneRow
            source={metaApiDataColumns.last_successful_sync.source}
            label={getColumnLabel(metaApiDataColumns.last_successful_sync)}
            showTime={true}
          />
        }
        {isAggregated ? (
          <TextField source={metaApiDataColumns.api_response.source} label={getColumnLabel(metaApiDataColumns.api_response)} />
        ) : (
          <FunctionField
            label={getColumnLabel(metaApiDataColumns.api_response)}
            source={metaApiDataColumns.api_response.source}
            render={record => (
              <Tooltip
                title={
                  <pre style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {formatJson(record.api_response)}
                  </pre>
                }
                placement='left'
                arrow
                classes={{ popper: 'custom-tooltip' }}
              >
                <div style={{
                  width: '280px',
                  wordWrap: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {record.api_response}
                </div>
              </Tooltip>
            )}
          />
        )}
      </DatagridConfigurable>
    </>
  );
};
